export type OutputBinding =
	| string
	| number
	| [number, number, number]
	| [number, number, number, number];

export type MaterialRoleKind = 'color' | 'scalar' | 'normal';

export type MaterialRoleContract = {
	names: readonly string[];
	label: string;
	viewer_channel: boolean;
	kind: MaterialRoleKind;
	default: number | readonly [number, number, number];
	default_image_format: 'rgba8' | 'rgba16' | 'gray8' | 'gray16' | null;
	unit_label: string | null;
	soft_range: readonly [number, number] | null;
};

/**
 * Compile-time mirror of the graph crate's authoritative contract. The worker
 * compares the complete JSON value with Rust at boot so drift fails loudly.
 */
export const MATERIAL_ROLES = [
	{
		names: ['base_color', 'albedo'],
		label: 'Base color',
		viewer_channel: true,
		kind: 'color',
		// Match the Rust f32 contract exactly so the worker's drift check is exact.
		default: [0.7058824, 0.7058824, 0.7058824],
		default_image_format: 'rgba8',
		unit_label: null,
		soft_range: null
	},
	{
		names: ['height'],
		label: 'Height',
		viewer_channel: true,
		kind: 'scalar',
		default: 0.5,
		default_image_format: 'gray16',
		unit_label: null,
		soft_range: [0, 1]
	},
	{
		names: ['normal'],
		label: 'Normal',
		viewer_channel: true,
		kind: 'normal',
		default: [0.5, 0.5, 1],
		default_image_format: 'rgba8',
		unit_label: null,
		soft_range: null
	},
	{
		names: ['roughness'],
		label: 'Roughness',
		viewer_channel: true,
		kind: 'scalar',
		default: 0.5,
		default_image_format: 'gray8',
		unit_label: null,
		soft_range: [0, 1]
	},
	{
		names: ['metallic'],
		label: 'Metallic',
		viewer_channel: true,
		kind: 'scalar',
		default: 0,
		default_image_format: 'gray8',
		unit_label: null,
		soft_range: [0, 1]
	},
	{
		names: ['emissive'],
		label: 'Emissive',
		viewer_channel: true,
		kind: 'color',
		default: [0, 0, 0],
		default_image_format: 'rgba8',
		unit_label: null,
		soft_range: null
	},
	{
		names: ['emission_luminance'],
		label: 'Emission luminance',
		viewer_channel: false,
		kind: 'scalar',
		default: 100,
		default_image_format: null,
		unit_label: 'cd/m²',
		soft_range: [0, 1000]
	},
	{
		names: ['ao'],
		label: 'AO',
		viewer_channel: true,
		kind: 'scalar',
		default: 1,
		default_image_format: 'gray8',
		unit_label: null,
		soft_range: [0, 1]
	},
	{
		names: ['opacity'],
		label: 'Opacity',
		viewer_channel: true,
		kind: 'scalar',
		default: 1,
		default_image_format: 'gray8',
		unit_label: null,
		soft_range: [0, 1]
	}
] as const satisfies readonly MaterialRoleContract[];

export type MaterialOutputRole = (typeof MATERIAL_ROLES)[number]['names'][0];
export const MATERIAL_OUTPUT_ROLES: readonly MaterialOutputRole[] = MATERIAL_ROLES.map(
	(role) => role.names[0]
);

export function materialRole(name: string): MaterialRoleContract | undefined {
	return MATERIAL_ROLES.find((role) => (role.names as readonly string[]).includes(name));
}

export function materialRoleDefault(name: string): Exclude<OutputBinding, string> | undefined {
	const value = materialRole(name)?.default;
	if (typeof value === 'number') return value;
	return value ? ([...value] as [number, number, number]) : undefined;
}

export function outputConstantDefault(
	name: string,
	kind: 'scalar' | 'rgba'
): Exclude<OutputBinding, string> {
	return materialRoleDefault(name) ?? (kind === 'scalar' ? 0 : [0, 0, 0, 1]);
}
